import { Component, OnInit, inject, effect } from '@angular/core';
import { Store } from '@ngxs/store';
import { GenericState } from 'src/app/store/generic.state';
import { SignupQuestionsSignal } from 'src/app/signal/signup-questions.signal';
import * as _ from 'underscore';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { filter, take } from 'rxjs';
import { LookupModel, MLookupModel } from 'src/app/models/lookupModel';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { ISalaryWorkPreference } from 'src/app/models/signup-questions.model';

@Component({
	selector: 'app-second-step',
	templateUrl: './second-step.component.html',
	styleUrls: ['./second-step.component.scss'],
	imports: [NzSelectModule, NzInputModule, NzCheckboxModule, NzSpaceModule, NzPopoverModule, FormsModule, ReactiveFormsModule, NgClass]
})
export class SecondStepComponent implements OnInit {
	private readonly signupQuestionsSignal = inject(SignupQuestionsSignal);
	private readonly genericActions$ = inject(Store).select(GenericState.getGeneric);
	
	public form: FormGroup;
	public preferredWorkSetups: MLookupModel[] = [];
	public availability: LookupModel[] = [];
	public contactOptions: string[] = ['Email', 'Phone'];
	public relocationOptions: string[] = [
		'I am willing to relocate',
		'I am NOT willing to relocate',
		'Depends on the salary'
	];

	public currencyOptions: Set<string> = new Set();
	private isFormUpdating = false;
	private lookupsLoaded = false;

	constructor() {
		// Effect to watch salary work preference from signal for form patching
		effect(() => {
			const salaryPref = this.signupQuestionsSignal.salaryWorkPreference();
			
			if (this.isFormUpdating && !_.isEmpty(salaryPref) && this.lookupsLoaded) {
				this.isFormUpdating = false;
				
				// Find work setup object by ID (convert to number for comparison)
				const workSetupObj = this.preferredWorkSetups.find(
					(ws) => Number(ws.id) === salaryPref.preferredWorkLocationId
				);
				
				// Find availability object by ID (convert to number for comparison)
				const availabilityObj = this.availability.find(
					(a) => Number(a.id) === salaryPref.workAvailabilityId
				);

				this.form?.patchValue({
					currency: salaryPref.currency || 'PHP',
					desiredSalary: this.formatSalary(salaryPref.expectedSalary?.toString() ?? ''),
					isFlexibleSalary: salaryPref.isSalaryNegotiable || false,
					workSetup: workSetupObj || null,
					relocation: salaryPref.workRelocation || null,
					availability: availabilityObj || null,
					preferredContact: salaryPref.waysToReachOut
						?.split(',')
						.filter((e: any) => !_.isEmpty(e))
						.map((e: string) => e.trim()) || null
				});
			}
		});
	}

	ngOnInit(): void {
		this.isFormUpdating = true;
		this.buildForm();
		this.setupLookups();
		this.checkFormValue();
	}

	private setupLookups(): void {
		this.genericActions$.pipe(
			filter((data: any) => !_.isEmpty(data.preferredWorkSetup) && !_.isEmpty(data.availability)),
			take(1)
		).subscribe((data) => {
			this.availability = data.availability;
			this.currencyOptions = this.mapCurrency(data.geoCountries);
			this.preferredWorkSetups = data.preferredWorkSetup.sort(
				(a: any, b: any) => b.option.localeCompare(a.option)
			);
			this.lookupsLoaded = true;
			
			// Patch form after lookups are loaded
			this.patchFormFromSignal();
		});
	}

	private patchFormFromSignal(): void {
		const salaryPref = this.signupQuestionsSignal.salaryWorkPreference();
		
		if (!_.isEmpty(salaryPref)) {
			this.isFormUpdating = false;
			
			// Find work setup object by ID (convert to number for comparison)
			const workSetupObj = this.preferredWorkSetups.find(
				(ws) => Number(ws.id) === salaryPref.preferredWorkLocationId
			);
			
			// Find availability object by ID (convert to number for comparison)
			const availabilityObj = this.availability.find(
				(a) => Number(a.id) === salaryPref.workAvailabilityId
			);

			this.form.patchValue({
				currency: salaryPref.currency || 'PHP',
				desiredSalary: this.formatSalary(salaryPref.expectedSalary?.toString() ?? ''),
				isFlexibleSalary: salaryPref.isSalaryNegotiable || false,
				workSetup: workSetupObj || null,
				relocation: salaryPref.workRelocation || null,
				availability: availabilityObj || null,
				preferredContact: salaryPref.waysToReachOut
					?.split(',')
					.filter((e: any) => !_.isEmpty(e))
					.map((e: string) => e.trim()) || null
			});
		}
	}

	private checkFormValue(): void {
		this.form.valueChanges.subscribe((value) => {
			this.patchStoreData(value);
		});
	}

	private patchStoreData(value: any): void {
		const salary = Number.parseInt(
			value.desiredSalary?.toString().replaceAll(',', '') || '0',
			10
		);

		const payload: Partial<ISalaryWorkPreference & { isFormValid?: boolean }> = {
			currency: value.currency,
			expectedSalary: salary,
			isSalaryNegotiable: value.isFlexibleSalary,
			preferredWorkLocationId: value.workSetup?.id || null,
			workRelocation: value.relocation,
			workAvailabilityId: value.availability?.id || null,
			waysToReachOut: value.preferredContact?.join(',') || null,
			isFormValid: this.form.valid
		};

		this.signupQuestionsSignal.patchSalaryPreferences(payload);
	}
	
	private mapCurrency(geo: any[]): Set<string> {
		const priorityCurrencies = ['PHP', 'USD', 'AUD', 'INR', 'COP'];
		return new Set(
			geo
				.map((item) => item.currency)
				.filter(Boolean)
				.sort((a, b) => {
					const indexA = priorityCurrencies.indexOf(a);
					const indexB = priorityCurrencies.indexOf(b);

					if (indexA !== -1 && indexB !== -1) return indexA - indexB;
					if (indexA !== -1) return -1;
					if (indexB !== -1) return 1;
					return a.localeCompare(b);
				})
		);
	}

	private formatSalary(value: string): string {
		return value
			.replaceAll(/\D/g, '')
			.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
	}

	public onSalaryChanged(value: string): void {
		const salary = this.formatSalary(value);
		this.form.patchValue({ desiredSalary: salary });
	}

	private buildForm(): void {
		this.form = new FormGroup({
			currency: new FormControl('PHP'),
			desiredSalary: new FormControl(null),
			isFlexibleSalary: new FormControl(false),
			workSetup: new FormControl(null, Validators.required),
			relocation: new FormControl(null),
			availability: new FormControl(null, Validators.required),
			preferredContact: new FormControl(null)
		});
	}
}
